import React, { useEffect, useState } from 'react';

import { MonitorOutlined, PhoneIphoneOutlined } from '@mui/icons-material';
import { Tab, Tabs } from '@mui/material';
import { Reader } from '@usewaypoint/email-builder';
import {
  Box,
  Loader,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from 'components';

import { DesignHelper } from './DesignHelper';
import { EditorBlock } from './DocumentViewer/EditorBlock';
import { InspectorDrawer } from './InspectorDrawer/InspectorDrawer';
import { ExportTemplate } from './TemplatePanel/ExportTemplate';
import { ImportTemplate } from './TemplatePanel/ImportTemplate';
import MainTabsGroup from './TemplatePanel/MainTabsGroup';
import { RevertTemplate } from './TemplatePanel/RevertTemplate';
import { SaveTemplate } from './TemplatePanel/SaveTemplate';

import { useSelector, useTranslation } from 'third-party';

import { useSettings } from 'modules/setting';

import { settingSelectors } from 'selectors';

import { APISettingName } from 'types/api';

const getBoxStyle = (screen: 'desktop' | 'mobile') => {
  if (screen == 'desktop') {
    return {
      height: '100%',
    };
  }

  return {
    margin: '32px auto',
    width: 370,
    height: 800,
    boxShadow:
      'rgba(33, 36, 67, 0.04) 0px 10px 20px, rgba(33, 36, 67, 0.04) 0px 2px 6px, rgba(33, 36, 67, 0.04) 0px 0px 1px',
  };
};

const DEFAULT_TEMPLATE = 'APISettingName.NewECheckReceiveEmail';

export const EmailDesigner = () => {
  const document = useSelector(settingSelectors.emailTemplate);
  const isEmailTemplateReady = useSelector(
    settingSelectors.isEmailTemplateReady,
  );
  const selectedMainTab = useSelector(settingSelectors.selectedMainTab);
  const selectedScreenSize = useSelector(settingSelectors.selectedScreenSize);
  const { setSelectedScreenSize, clearEmailTemplate } = useSettings();

  const handleScreenSizeChange = (_: unknown, value: string) => {
    switch (value) {
      case 'mobile':
      case 'desktop':
        setSelectedScreenSize(value);
        return;
      default:
        setSelectedScreenSize('desktop');
    }
  };

  const { t } = useTranslation();
  const { loadEmailTemplate, setSelectedTemplate } = useSettings();
  const [selectedTemplate, setSelectedTemplateLocal] =
    useState(DEFAULT_TEMPLATE);

  useEffect(() => {
    loadEmailTemplate(DEFAULT_TEMPLATE);
    setSelectedTemplateLocal(DEFAULT_TEMPLATE);
  }, [loadEmailTemplate]);

  const handleTemplateChange = (template: APISettingName) => {
    loadEmailTemplate(template);
    setSelectedTemplate(template);
    setSelectedTemplateLocal(template);
  };

  useEffect(() => {
    return () => {
      clearEmailTemplate();
    };
  }, [clearEmailTemplate]);

  const renderMainPanel = () => {
    if (!isEmailTemplateReady) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <Loader />
        </Box>
      );
    }
    switch (selectedMainTab) {
      case 'editor':
        return (
          <Box sx={getBoxStyle(selectedScreenSize)}>
            <EditorBlock id="root" />
          </Box>
        );
      case 'preview':
        return (
          <Box sx={getBoxStyle(selectedScreenSize)}>
            <Reader document={document} rootBlockId="root" />
          </Box>
        );
    }
  };

  return (
    <>
      <Stack
        sx={{
          height: 49,
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: 'white',
          position: 'sticky',
          top: 0,
          zIndex: 'appBar',
          px: 1,
        }}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Stack
          px={2}
          direction="row"
          gap={2}
          width="100%"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" spacing={2}>
            <MainTabsGroup />
          </Stack>
          <Stack direction="row" spacing={2}>
            <Tabs
              value={selectedTemplate}
              onChange={(_, value) =>
                handleTemplateChange(value as APISettingName)
              }
              sx={{ minWidth: '10rem' }}
            >
              <Tab
                value={APISettingName.NewECheckReceiveEmail}
                label={t('emailDesigner.template.NewECheckReceiveEmail')}
              />
              <Tab
                value={APISettingName.AddedToOrganizationEmail}
                label={t('emailDesigner.template.AddedToOrganizationEmail')}
              />
            </Tabs>
            <SaveTemplate />
            <RevertTemplate />
            <ExportTemplate />
            <ImportTemplate />
            <DesignHelper />
            <ToggleButtonGroup
              value={selectedScreenSize}
              exclusive
              size="small"
              onChange={handleScreenSizeChange}
            >
              <ToggleButton value="desktop">
                <Tooltip title="Desktop view">
                  <MonitorOutlined fontSize="small" />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="mobile">
                <Tooltip title="Mobile view">
                  <PhoneIphoneOutlined fontSize="small" />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Stack>
      </Stack>
      <Box display="flex">
        <Box
          sx={{
            height: 'calc(100vh - 10rem)',
            overflow: 'auto',
            flexGrow: 1,
          }}
        >
          {renderMainPanel()}
        </Box>
        <Box
          sx={{
            height: 'calc(100vh - 10rem)',
            overflow: 'auto',
          }}
          overflow="scroll"
        >
          <InspectorDrawer />
        </Box>
      </Box>
    </>
  );
};
